import { useState, useContext, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'
import { cn } from '@/lib/utils'

const OPTIONS = [
  {
    value: 'confirm',
    label: 'Confirm (default)',
    description:
      "After a passing revalidation run, propose a one-click 'Close the loop'; a person applies it.",
  },
  {
    value: 'auto',
    label: 'Auto',
    description:
      'Apply closure immediately when a triggered run passes: challenged evidence is superseded by the run’s AAR evidence and the drift event resolves.',
  },
]

/**
 * RevalidationClosurePanel — customer-facing settings card for revalidation
 * closure mode (R44). Placed directly after InvalidationRulesPanel in the
 * Settings → Organization tab.
 *
 * Per CLAUDE.md placement rule: customer-facing → Settings, not Admin.
 */
export default function RevalidationClosurePanel() {
  const authContext = useContext(AuthContext)
  const org = authContext.user?.organization

  const [mode, setMode] = useState('confirm')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  const loadMode = useCallback(async () => {
    if (!org?.id) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${org.id}/settings/revalidation-closure`,
        { method: 'GET' },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to load revalidation closure settings')
        return
      }
      setMode(data.mode || 'confirm')
    } catch (e) {
      setError(e.message || 'Failed to load revalidation closure settings')
    } finally {
      setLoading(false)
    }
  }, [org?.id, authContext])

  useEffect(() => {
    loadMode()
  }, [loadMode])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${org.id}/settings/revalidation-closure`,
        {
          method: 'PUT',
          body: JSON.stringify({ mode }),
        },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to save revalidation closure settings')
        return
      }
      setMode(data.mode || mode)
      setSaved(true)
      setTimeout(() => setSaved(false), 10000)
      toast.success('Revalidation closure mode saved')
    } catch (e) {
      setError(e.message || 'Failed to save revalidation closure settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-4">
        <h3 className="text-sm font-semibold">Revalidation Closure</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          What happens when a targeted revalidation run passes. R44.
        </p>
      </div>

      {saved && (
        <Alert className="mb-3 border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="size-4" />
          <AlertDescription>Revalidation closure mode saved</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setMode(opt.value)}
                className={cn(
                  'flex flex-col items-start gap-0.5 rounded-md border p-3 text-left transition-colors',
                  mode === opt.value
                    ? 'border-foreground bg-accent'
                    : 'border-border hover:bg-accent/50',
                )}
                aria-pressed={mode === opt.value}
              >
                <span className="text-sm font-medium">{opt.label}</span>
                <span className="text-sm text-muted-foreground">
                  {opt.description}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-1 size-3.5 animate-spin" />
                  Saving&hellip;
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
